import React, { useRef, useState } from 'react';

import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { isBotClient } from '@shared/utils/botDetection/isBotClient';
import { isPageUnloading } from '@shared/utils/pageUnloading';

import { useAutoLoadComplete } from '../hooks/useAutoLoadComplete';
import { useLoadEndEffect } from '../hooks/useLoadEndEffect';
import { useLoadStartEffect } from '../hooks/useLoadStartEffect';
import { useLoadWatchdog } from '../hooks/useLoadWatchdog';
import { useComponentHealthProvider } from '../ComponentHealthProvider';
import type { ComponentHealthState, ComponentInfo } from '../types';
import { StateType } from '../types';
import type { InternalContextValue } from '../types';
import { ErrorBoundary } from './ErrorBoundary';
import { generateLoadId } from './generateLoadId';
import { useComponentLog } from '../hooks/useComponentLog';
import { ACTION_COMPONENT_LOAD_ABANDONED, ACTION_COMPONENT_LOAD_TIMEOUT_BOT, COMPONENT_LOADING_TIMEOUT_MS, COMPONENT_SLOW_LOG_TIMEOUT_MS } from '../constants';

export interface ComponentHealthWrapperProps {
  readonly name: string;
  readonly internalContext: React.Context<InternalContextValue | undefined>;
  readonly children: React.ReactNode;
  readonly errorComponent?: React.ComponentType<{}>;
  readonly forceErrorUIOnTimeout?: boolean; // Force to show error component on timeout
  readonly timeout?: number; // component health timeout in ms
  readonly slowLogTimeout?: number; // component health slow timeout in ms
}

export const ComponentHealthWrapper: React.FC<ComponentHealthWrapperProps> = ({
  name,
  internalContext: InternalContext,
  children,
  errorComponent: ErrorComponent,
  forceErrorUIOnTimeout,
  timeout = COMPONENT_LOADING_TIMEOUT_MS,
  slowLogTimeout = COMPONENT_SLOW_LOG_TIMEOUT_MS,
}) => {
  const errorBoundaryRef = useRef<ErrorBoundary>(null);
  const componentInfoRef = useRef<ComponentInfo>({
    componentName: name,
    loadId: generateLoadId(),
    loadStartTime: Date.now(),
  });
  const [componentHealthState, setComponentHealthState] = useState<ComponentHealthState>({
    type: StateType.Loading,
  });

  useLoadStartEffect(componentInfoRef);
  useLoadEndEffect(componentInfoRef, componentHealthState);
  const { onComponentLog } = useComponentLog(componentInfoRef, componentHealthState);

  const onLoadComplete = useEventCallback(() => {
    setComponentHealthState(prevState => {
      if (prevState.type === 'loading') {
        return {
          type: StateType.Complete,
        };
      }

      return prevState;
    });
  });
  const onLoadFailure = useEventCallback((reason: unknown, context?: object, forceErrorUI?: boolean) => {
    setComponentHealthState({
      type: StateType.Failure,
      reason,
      context: {
        ...context,
        // don't permit overriding component name
        componentName: componentInfoRef.current.componentName,
      },
    });
    // Manually trigger error UI
    if (forceErrorUI) {
      errorBoundaryRef.current?.forceErrorUI();
    }
  });
  const onRegisterWithProvider = useAutoLoadComplete(onLoadComplete);

  const { onAction } = useComponentHealthProvider();
  // A timeout means a guest watched a load that never finished. Hidden tabs and pages being navigated
  // away from stall loads with nobody waiting, so the watchdog counts only visible time and waits out
  // an unload. Crawlers keep the watchdog and its error UI, but their timeouts are a page action, not
  // an error.
  useLoadWatchdog({
    isLoading: componentHealthState.type === StateType.Loading,
    timeout,
    onTimeout: () => {
      if (!isBotClient()) {
        onLoadFailure('timeout', undefined, forceErrorUIOnTimeout);
        return;
      }
      const { componentName, loadId } = componentInfoRef.current;
      onAction(ACTION_COMPONENT_LOAD_TIMEOUT_BOT, { componentName, loadId });
      if (forceErrorUIOnTimeout) {
        errorBoundaryRef.current?.forceErrorUI();
      }
    },
    shouldPostpone: isPageUnloading,
    onAbandon: visibleMs => {
      const { componentName, loadId, loadStartTime } = componentInfoRef.current;
      onAction(ACTION_COMPONENT_LOAD_ABANDONED, { componentName, loadId, visibleMs, totalMs: Date.now() - loadStartTime });
    },
  });

  useLoadWatchdog({
    isLoading: componentHealthState.type === StateType.Loading,
    timeout: slowLogTimeout,
    onTimeout: () => {
      onComponentLog('slow', { duration: slowLogTimeout });
    },
    shouldPostpone: isPageUnloading,
  });

  return (
    <ErrorBoundary
      ref={errorBoundaryRef}
      onCaughtError={onLoadFailure}
      errorComponent={ErrorComponent}
    >
      <InternalContext.Provider value={{ onLoadComplete, onLoadFailure, onRegisterWithProvider }}>{children}</InternalContext.Provider>
    </ErrorBoundary>
  );
};
